import { translate, MagicConfigMixin } from '@gig/magic-common';
import { __extends, __awaiter, __generator, __assign, __decorate, __metadata } from 'tslib';
import { namespace } from 'vuex-class';
import { Prop, Emit, Watch, Component, Mixins } from 'vue-property-decorator';
import { ESubmitState } from '../../types/form-builder.interface.js';
import FormBuilderPartial from '../form-builder-partial/form-builder.partial.vue.js';
import { getTriggersMap, extractConfig } from '../../util/form-builder.utils.js';
import '../../util/validators/required.js';
import '../../util/validators/min-length.js';
import '../../util/validators/max-length.js';
import '../../util/validators/contains-letter.js';
import '../../util/validators/contains-number.js';
import '../../util/validators/is-email.js';
import '../../util/validators/is-numeric.js';
import '../../util/validators/is-valid-iban.js';
import '../../util/validators/is-non-sh-postal-code.js';
import '../../util/validators/not-only-whitespaces.js';
import '../../util/validators/is-valid-canadian-postal-code.js';
import '../../util/validators/exact-length.js';
import '../../util/validators/contains-only-letters.js';
import '../../util/pinned-countries.js';
import { FORM_BUILDER_NAMESPACE } from '../../store/modules/form-builder/form-builder.constants.js';
import '../../store/modules/form-builder/form-builder.store.js';
import { store } from '../../store/index.js';
import deepmerge from 'deepmerge';

var formBuilderModule = namespace(FORM_BUILDER_NAMESPACE);
var DEFAULT_INITIAL_STEP = '0';
var FormBuilderComponent = /** @class */ (function (_super) {
    __extends(FormBuilderComponent, _super);
    function FormBuilderComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.triggerMap = new Map();
        _this.nextStepBlocked = false;
        _this.initialised = false;
        _this.preloadedData = {};
        return _this;
    }
    FormBuilderComponent.prototype.created = function () {
        this.resetState(this.stateKey);
        this.initializeState({ stateKey: this.stateKey, steps: this.config });
        this.loadForm();
        this.triggerMap = getTriggersMap(this.config);
    };
    Object.defineProperty(FormBuilderComponent.prototype, "formDataModel", {
        get: function () {
            return this.stateFormDataModel(this.stateKey);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "submitDataModel", {
        get: function () {
            return this.stateSubmitDataModel(this.stateKey);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "isSubmitting", {
        /**
         * true is the form has been submitted but async operation is still happening
         */
        get: function () {
            return this.submitState === ESubmitState.SUBMITTING;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "step", {
        /**
         * Part of config for the current step
         * @returns {IFormBuilderStepConfiguration}
         */
        get: function () {
            var _this = this;
            if (this.currentStep === DEFAULT_INITIAL_STEP) {
                return this.config[0];
            }
            return (this.config.find(function (configStep) { return configStep.path === _this.currentStep; }) || this.config[0]);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "components", {
        /**
         * Components for the current step (non-nullish)
         * @returns {IFormBuilderElement[]}
         */
        get: function () {
            return this.step.components;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "dataComponents", {
        /**
         * Data fields for the current step (non-nullish)
         * @returns {IFormBuilderElement[]}
         * @private
         */
        get: function () {
            var flattenedComponents = extractConfig(this.components);
            return flattenedComponents.filter(function (component) { return component.isDataField; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "shouldBlockNextStep", {
        /**
         * Should block the next step, which is the case when any of the fields from
         * the current step are invalid.
         * @returns {boolean}
         */
        get: function () {
            var _this = this;
            return this.dataComponents.some(function (component) {
                var formData = _this.formDataModel[component.id];
                return _this.isFormDataInvalid(formData);
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "shouldShowStepError", {
        /**
         * Should show the step error message, which is the case when any of the fields from
         * the current step are invalid and touched by the user
         * @returns {boolean}
         */
        get: function () {
            var _this = this;
            if (this.step.alwaysShowErrorMessage) {
                return true;
            }
            return this.dataComponents.some(function (component) {
                var formData = _this.formDataModel[component.id];
                return _this.isFormDataInvalid(formData) && formData.touched;
            });
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Checks if the form field is valid or not
     * @param formData
     * @private
     */
    FormBuilderComponent.prototype.isFormDataInvalid = function (formData) {
        return (formData &&
            !formData.hidden &&
            !formData.disabled &&
            (!formData.isValid || formData.isValidationPending));
    };
    Object.defineProperty(FormBuilderComponent.prototype, "nextButtonDisabled", {
        /**
         * Should show the submit button, which is the case when all the fields from
         * the current step are valid and touched by the user
         * @returns {boolean}
         */
        get: function () {
            var _a;
            if ((_a = this.step.nextButton) === null || _a === void 0 ? void 0 : _a.alwaysEnabled) {
                return false;
            }
            else {
                return this.shouldBlockNextStep;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "buttonText", {
        get: function () {
            var _a = this.step.nextButton, isTranslationKey = _a.isTranslationKey, text = _a.text;
            return !isTranslationKey ? text : translate(text);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "errorMessageText", {
        get: function () {
            var _a = this.step, isTranslationKey = _a.isTranslationKey, errorMessage = _a.errorMessage;
            return !isTranslationKey ? errorMessage : translate(errorMessage);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FormBuilderComponent.prototype, "allComponents", {
        /**
         * All components in the config, flattened. The initial purpose was to
         * map all the data fields (isDataField === true).
         * @returns {IFormBuilderElement[]}
         * @private
         */
        get: function () {
            var all = this.config.flatMap(function (step) { return step.components; });
            return extractConfig(all);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Loads the form initial state and preloaded properties
     * (e.g. phone prefixes).
     */
    FormBuilderComponent.prototype.loadForm = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.setFormDefaultState();
                        this.setFormInitialState();
                        this.initialised = true;
                        return [4 /*yield*/, this.setPreloadedProperties()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Action triggered when the button at the bottom is clicked.
     * It will either route to the next step or submit the form.
     * @param event
     * @private
     */
    FormBuilderComponent.prototype.onSubmit = function (event) {
        event.preventDefault();
        if (this.shouldBlockNextStep) {
            this.nextStepBlocked = true;
            this.forceValidationsToDisplay();
            return;
        }
        if (this.currentStepIndex < this.config.length - 1) {
            // we still have form steps, let's route to the next
            // step changing the url
            this.routeToStep(this.currentStepIndex + 1);
        }
        else {
            this.submitForm();
        }
    };
    /**
     * Forces the validations to display on the form fields.
     * This is needed when the user clicks the next button
     * and the form is invalid.
     * We need to do this because the form fields are not
     * re-rendered when the form is invalid.
     * We need to force the validations to display on the form fields
     * so the user can see the error messages.
     * @private
     */
    FormBuilderComponent.prototype.forceValidationsToDisplay = function () {
        var _this = this;
        this.dataComponents.forEach(function (field) {
            _this.setField({
                stateKey: _this.stateKey,
                field: { id: field.id, touched: true },
            });
        });
    };
    Object.defineProperty(FormBuilderComponent.prototype, "currentStepIndex", {
        /**
         * Returns the index of the current step in the config
         * @returns {number}
         * @private
         */
        get: function () {
            var configIndex = this.config.indexOf(this.step);
            return configIndex !== -1 ? configIndex : 0;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Emits the next-step event with the path of the next step
     * @param stepIndex The index of the next step
     * @returns The path of the next step
     */
    FormBuilderComponent.prototype.routeToStep = function (stepIndex) {
        return this.config[stepIndex].path;
    };
    FormBuilderComponent.prototype.submitForm = function () {
        var _this = this;
        return Object.keys(this.submitDataModel).reduce(function (acc, field) {
            var _a, _b;
            var _c, _d;
            var config = _this.allComponents.find(function (component) { return component.id === field; });
            var value = (config === null || config === void 0 ? void 0 : config.converter)
                ? config.converter(_this.submitDataModel)
                : (config === null || config === void 0 ? void 0 : config.serverId)
                    ? (_a = {}, _a[config.serverId] = (_c = _this.submitDataModel[field]) === null || _c === void 0 ? void 0 : _c.value, _a) : (_b = {}, _b[field] = (_d = _this.submitDataModel[field]) === null || _d === void 0 ? void 0 : _d.value, _b);
            return deepmerge(acc, value);
        }, {});
    };
    /**
     * Sets the default field value (if exists) on the model,
     * otherwise sets by default the first option available
     * @private
     */
    FormBuilderComponent.prototype.setFormDefaultState = function () {
        var _a;
        for (var _i = 0, _b = this.allComponents; _i < _b.length; _i++) {
            var component = _b[_i];
            var id = component.id, isDataField = component.isDataField;
            if (!isDataField) {
                continue;
            }
            if (this.formDataModel.hasOwnProperty(id) &&
                this.formDataModel[id].value === null &&
                ((_a = component.properties) === null || _a === void 0 ? void 0 : _a.value)) {
                this.setField({
                    stateKey: this.stateKey,
                    field: {
                        id: id,
                        value: component.properties.value,
                    },
                });
            }
        }
    };
    /**
     * Sets the preloaded properties for the fields that have
     * `preloadProperties` function defined in the config.
     * E.g. phone-config.ts for loading country prefixes.
     * Once the data is loaded, it also sets the default value
     */
    FormBuilderComponent.prototype.setPreloadedProperties = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getItemsWithPreloadedData()];
                    case 1:
                        (_a.sent()).forEach(function (_a) {
                            var item = _a.item, data = _a.data;
                            _this.preloadedData[item.id] = data;
                            _this.updatePreloadedModel(item);
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Retrieves the items with preloaded data by calling their
     * `preloadProperties` function and returning the results.
     * @returns {Promise<{ item: IFormBuilderElement, data: any }[]>}
     */
    FormBuilderComponent.prototype.getItemsWithPreloadedData = function () {
        return __awaiter(this, void 0, void 0, function () {
            var promises;
            var _this = this;
            return __generator(this, function (_a) {
                promises = this.allComponents
                    .filter(function (item) { return item.preloadProperties; })
                    .map(function (item) { return __awaiter(_this, void 0, void 0, function () {
                    var data, _a;
                    var _b, _c;
                    return __generator(this, function (_d) {
                        switch (_d.label) {
                            case 0:
                                _d.trys.push([0, 2, , 3]);
                                return [4 /*yield*/, ((_b = item.preloadProperties) === null || _b === void 0 ? void 0 : _b.call(item))];
                            case 1:
                                data = (_c = (_d.sent())) !== null && _c !== void 0 ? _c : {};
                                return [2 /*return*/, { item: item, data: data }];
                            case 2:
                                _a = _d.sent();
                                return [2 /*return*/, { item: item, data: {} }];
                            case 3: return [2 /*return*/];
                        }
                    });
                }); });
                return [2 /*return*/, Promise.all(promises)];
            });
        });
    };
    /**
     * Sets the form state with the updated field
     * @param field
     */
    FormBuilderComponent.prototype.checkForFieldValidationError = function () {
        if (!this.updatedField) {
            return;
        }
        for (var key in this.updatedField) {
            this.setField({
                stateKey: this.stateKey,
                field: this.updatedField[key],
            });
        }
    };
    /**
     * Sets the form state with the initial state
     * (if exists) for the fields that have been touched by the user.
     * @private
     */
    FormBuilderComponent.prototype.setFormInitialState = function () {
        if (!this.initialState) {
            return;
        }
        for (var _i = 0, _a = this.allComponents; _i < _a.length; _i++) {
            var component = _a[_i];
            if (!component.isDataField) {
                continue;
            }
            var id = component.id;
            if (this.fieldHasInitialState(id)) {
                this.setField({
                    stateKey: this.stateKey,
                    field: this.initialState[id],
                });
            }
        }
    };
    /**
     * Sets the default field value (if exists) on the model,
     * otherwise sets by default the first option available
     * @param field
     */
    FormBuilderComponent.prototype.updatePreloadedModel = function (field) {
        if (this.formDataModel.hasOwnProperty(field.id)) {
            // if the field has an initial state, it's already written in the store
            // from the `setFormInitialState` method
            // however, we still need to update it again (with the same value)
            // we need to re-render the form with the preloaded data
            // and form re-renders only on specific conditions, like value update
            var defaultValue = this.getDefaultValue(field);
            var updatedField = this.fieldHasInitialState(field.id)
                ? {
                    id: field.id,
                    value: this.initialState[field.id].value,
                }
                : {
                    id: field.id,
                    value: defaultValue,
                    isValidationPending: false,
                    isValid: field.isOptional || defaultValue !== null,
                };
            this.setField({
                stateKey: this.stateKey,
                field: updatedField,
            });
            this.invokeInitialFieldTriggers(field.id, updatedField.value);
        }
    };
    /**
     * Checks if the field has an initial state and has been touched by the user
     * @param id
     * @private
     * @returns {boolean}
     */
    FormBuilderComponent.prototype.fieldHasInitialState = function (id) {
        var _a;
        return (((_a = this.initialState) === null || _a === void 0 ? void 0 : _a.hasOwnProperty(id)) &&
            this.initialState[id].touched);
    };
    /**
     * Retrieves the default value for the field
     * @param field
     * @private
     * @returns {string | null}
     */
    FormBuilderComponent.prototype.getDefaultValue = function (field) {
        var _a, _b, _c, _d, _e, _f, _g;
        return ((_g = (_d = (_b = (_a = field.properties) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : (_c = this.preloadedData[field.id]) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : (_f = (_e = this.initialState) === null || _e === void 0 ? void 0 : _e[field.id]) === null || _f === void 0 ? void 0 : _f.value) !== null && _g !== void 0 ? _g : null);
    };
    FormBuilderComponent.prototype.emitValue = function (changed) {
        return {
            form: this.formDataModel,
            changed: changed,
        };
    };
    /**
     * Emitted when a field inside form builder emits a `field-touched` event
     * This event contains the value and the validity state of the field
     * @param event
     * @private
     */
    FormBuilderComponent.prototype.onFieldTouched = function (payload) {
        var fieldId = payload.fieldId, isValid = payload.isValid, error = payload.error, isValidationPending = payload.isValidationPending;
        if (this.formDataModel.hasOwnProperty(fieldId) &&
            !this.formDataModel[fieldId].touched) {
            this.setField({
                stateKey: this.stateKey,
                field: {
                    id: fieldId,
                    touched: true,
                    isValid: isValid,
                    error: error,
                    isValidationPending: isValidationPending,
                },
            });
        }
    };
    FormBuilderComponent.prototype.onFieldStateUpdate = function (payload) {
        var _a = payload.field, id = _a.id, setValueTo = _a.setValueTo, state = payload.state;
        var fieldId = setValueTo || id;
        this.setField({
            stateKey: this.stateKey,
            field: __assign({ id: fieldId }, state),
        });
    };
    /**
     * Emitted when a field inside form builder emits a `value-update` event
     * This event contains the value and the validity state of the field
     * @param event
     * @private
     */
    FormBuilderComponent.prototype.onValueUpdate = function (payload) {
        var value = payload.value, _a = payload.field, id = _a.id, setValueTo = _a.setValueTo, isValid = payload.isValid, error = payload.error, isValidationPending = payload.isValidationPending, touched = payload.touched;
        // if setValueTo is set, we should update that model id rather that the actual id
        var fieldId = setValueTo || id;
        this.setField({
            stateKey: this.stateKey,
            field: {
                id: fieldId,
                value: value,
                touched: touched,
                isValid: isValid,
                error: error,
                isValidationPending: isValidationPending,
            },
        });
        this.invokeFieldTriggers(id, value);
        this.emitValue({ id: id, value: value, isValid: isValid });
    };
    /**
     * If current field has declared triggers, invoke them passing the changed value as parameter
     * together with model context and setField function.
     */
    FormBuilderComponent.prototype.invokeFieldTriggers = function (fieldId, changedValue) {
        if (this.triggerMap.has(fieldId)) {
            var fxs = this.triggerMap.get(fieldId);
            if (fxs) {
                this.invokeTriggers(fxs, changedValue);
            }
        }
    };
    /**
     * Invoke Initial Field Triggers for the field
     * Only invoke triggers for fields that have not been touched and do not have initial value
     * (to avoid overwriting user input or initial state)
     * @param fieldId
     * @param changedValue
     * @private
     * @returns {boolean} - Returns true if triggers were invoked, false otherwise
     */
    FormBuilderComponent.prototype.invokeInitialFieldTriggers = function (fieldId, changedValue) {
        var _this = this;
        if (this.triggerMap.has(fieldId)) {
            var fxs = this.triggerMap.get(fieldId);
            if (fxs) {
                var filtered = fxs.filter(function (_a) {
                    var _b, _c;
                    var field = _a.field;
                    var config = _this.allComponents.find(function (c) { return c.id === field; });
                    return (!((_c = (_b = _this.initialState) === null || _b === void 0 ? void 0 : _b[field]) === null || _c === void 0 ? void 0 : _c.touched) &&
                        !_this.getDefaultValue(config));
                });
                this.invokeTriggers(filtered, changedValue);
            }
        }
    };
    /**
     * Invoke the triggers functions
     * @param fxs
     * @param changedValue
     * @returns {void}
     */
    FormBuilderComponent.prototype.invokeTriggers = function (fxs, changedValue) {
        var _this = this;
        fxs.forEach(function (_a) {
            var fx = _a.fx;
            return __awaiter(_this, void 0, void 0, function () {
                var _b;
                var _this = this;
                return __generator(this, function (_c) {
                    switch (_c.label) {
                        case 0:
                            _c.trys.push([0, 2, , 3]);
                            return [4 /*yield*/, fx(changedValue, this.formDataModel, function (field) {
                                    _this.setField({
                                        stateKey: _this.stateKey,
                                        field: field,
                                    });
                                })];
                        case 1:
                            _c.sent();
                            return [3 /*break*/, 3];
                        case 2:
                            _b = _c.sent();
                            return [2 /*return*/];
                        case 3: return [2 /*return*/];
                    }
                });
            });
        });
    };
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], FormBuilderComponent.prototype, "initialState", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Array)
    ], FormBuilderComponent.prototype, "config", void 0);
    __decorate([
        Prop({ type: String, default: DEFAULT_INITIAL_STEP }),
        __metadata("design:type", String)
    ], FormBuilderComponent.prototype, "currentStep", void 0);
    __decorate([
        Prop({ type: String, default: 'shared' }),
        __metadata("design:type", String)
    ], FormBuilderComponent.prototype, "stateKey", void 0);
    __decorate([
        Prop({ type: Number }),
        __metadata("design:type", Number)
    ], FormBuilderComponent.prototype, "submitState", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], FormBuilderComponent.prototype, "updatedField", void 0);
    __decorate([
        formBuilderModule.Action('initializeState'),
        __metadata("design:type", Object)
    ], FormBuilderComponent.prototype, "initializeState", void 0);
    __decorate([
        formBuilderModule.Action('setField'),
        __metadata("design:type", Object)
    ], FormBuilderComponent.prototype, "setField", void 0);
    __decorate([
        formBuilderModule.Getter('formDataModel'),
        __metadata("design:type", void 0)
    ], FormBuilderComponent.prototype, "stateFormDataModel", void 0);
    __decorate([
        formBuilderModule.Getter('submitDataModel'),
        __metadata("design:type", void 0)
    ], FormBuilderComponent.prototype, "stateSubmitDataModel", void 0);
    __decorate([
        formBuilderModule.Action('resetState'),
        __metadata("design:type", Object)
    ], FormBuilderComponent.prototype, "resetState", void 0);
    __decorate([
        Emit('next-step'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Number]),
        __metadata("design:returntype", void 0)
    ], FormBuilderComponent.prototype, "routeToStep", null);
    __decorate([
        Emit('form-submit'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], FormBuilderComponent.prototype, "submitForm", null);
    __decorate([
        Watch('updatedField'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], FormBuilderComponent.prototype, "checkForFieldValidationError", null);
    __decorate([
        Emit('form-change'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], FormBuilderComponent.prototype, "emitValue", null);
    FormBuilderComponent = __decorate([
        Component({
            name: 'form-builder',
            inheritAttrs: false,
            store: store,
            components: {
                'form-builder-partial': FormBuilderPartial,
            },
        })
    ], FormBuilderComponent);
    return FormBuilderComponent;
}(Mixins(MagicConfigMixin)));

export default FormBuilderComponent;
